import React from "react";
import { GridNode, GridEdge, HoverElement } from "../../types";
import KeyIcon from "./KeyIcon";
import ColumnHeader from "./ColumnHeader";

interface Props {
  buses: GridNode[];
  lines: GridEdge[];
  hoverElement: HoverElement | null;
  onElementHover: (element: HoverElement | null) => void;
  selectedBusId?: string | null;
  onBusSelect?: (nodeId: string) => void;
}

function BusTable({ buses, lines, hoverElement, onElementHover, selectedBusId, onBusSelect }: Props) {
  const tableStyle = {
    width: "100%",
    borderCollapse: "collapse" as const,
    fontSize: "12px",
    marginTop: "10px",
  };

  const thStyle = {
    background: "#f5f5f5",
    padding: "8px",
    textAlign: "left" as const,
    borderBottom: "1px solid #ddd",
    fontWeight: "bold" as const,
  };

  const tdStyle = {
    padding: "6px 8px",
    borderBottom: "1px solid #eee",
  };

  if (buses.length === 0) {
    return (
      <p style={{ padding: "20px", textAlign: "center", color: "#666" }}>
        No buses in the grid
      </p>
    );
  }

  return (
    <table style={tableStyle}>
      <thead>
        <tr>
          <th style={thStyle} title="Show LMP curve">LMP</th>
          <ColumnHeader column="id" style={thStyle}>ID<KeyIcon /></ColumnHeader>
          <th style={thStyle}>Voltage (pu)</th>
          <th style={thStyle}>LMP ($/MWh)</th>
          <th style={thStyle}>Position</th>
          <th style={thStyle}>Connected Lines</th>
        </tr>
      </thead>
      <tbody>
        {buses.map((bus) => {
          const connectedLines = lines
            .filter((line) => line.from === bus.id || line.to === bus.id)
            .map((line) => line.id)
            .join(", ");

          const isHighlighted = hoverElement?.type === 'Node' && hoverElement?.id === bus.id;
          return (
            <tr 
              key={bus.id}
              onMouseEnter={() => onElementHover({ type: 'Node', id: bus.id })}
              onMouseLeave={() => onElementHover(null)}
              style={{ backgroundColor: isHighlighted ? '#FFD70030' : 'transparent' }}
            >
              <td style={{ ...tdStyle, textAlign: 'center' }}>
                <input
                  type="radio"
                  name="lmp-bus-select"
                  checked={selectedBusId === bus.id}
                  onChange={() => onBusSelect?.(bus.id)}
                  onClick={(e) => e.stopPropagation()}
                  style={{ cursor: 'pointer' }}
                  title="Show LMP curve for this bus"
                />
              </td>
              <td style={tdStyle}>{bus.id}</td>
              <td style={tdStyle}>{bus.voltage?.toFixed(3) || "N/A"}</td>
              <td style={{...tdStyle, color: '#FF6600', fontWeight: 'bold'}}>
                {bus.lmp !== undefined ? `$${bus.lmp.toFixed(2)}` : "N/A"}
              </td>
              <td style={tdStyle}>
                ({bus.x.toFixed(0)}, {bus.y.toFixed(0)})
              </td>
              <td style={tdStyle}>{connectedLines || "None"}</td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

export default BusTable;